import { useEffect, useRef, useState } from "react";
import { motion, useMotionValue, useSpring } from "framer-motion";

/**
 * Editorial custom cursor.
 *
 * - Two layers: a fine dot (fast) + a soft ring (spring-lagged).
 * - Ring scales up on links/buttons; picks up an "OPEN" or "→" label
 *   when hovering media/CTAs.
 * - Hidden entirely on touch devices (pointer:coarse) — mobile users get
 *   the native tap targets.
 */
export default function CustomCursor() {
  const [enabled, setEnabled] = useState(false);
  const [variant, setVariant] = useState("default"); // default | link | media | text
  const [label, setLabel] = useState("");

  // Raw pointer coords
  const x = useMotionValue(-100);
  const y = useMotionValue(-100);
  // Springed ring coords (slight lag)
  const ringX = useSpring(x, { stiffness: 420, damping: 32, mass: 0.35 });
  const ringY = useSpring(y, { stiffness: 420, damping: 32, mass: 0.35 });

  useEffect(() => {
    // Only enable on fine-pointer devices (i.e. mouse users).
    const mq = window.matchMedia("(pointer: fine)");
    setEnabled(mq.matches);
    const onChange = (e) => setEnabled(e.matches);
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);

  useEffect(() => {
    if (!enabled) return;

    const onMove = (e) => {
      x.set(e.clientX);
      y.set(e.clientY);
    };

    const onOver = (e) => {
      const t = e.target;
      if (!(t instanceof Element)) return;
      const link = t.closest(
        "a, button, [role='button'], [data-cursor='link']"
      );
      const media = t.closest("[data-cursor='media']");
      const text = t.closest("input, textarea, [contenteditable='true']");
      if (media) {
        setVariant("media");
        setLabel(media.getAttribute("data-cursor-label") || "VEDI");
      } else if (link) {
        setVariant("link");
        setLabel(link.getAttribute("data-cursor-label") || "");
      } else if (text) {
        setVariant("text");
        setLabel("");
      } else {
        setVariant("default");
        setLabel("");
      }
    };

    document.addEventListener("mousemove", onMove, { passive: true });
    document.addEventListener("mouseover", onOver, { passive: true });
    // Hide default cursor globally when enabled.
    document.documentElement.classList.add("has-custom-cursor");
    return () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseover", onOver);
      document.documentElement.classList.remove("has-custom-cursor");
    };
  }, [enabled, x, y]);

  if (!enabled) return null;

  const ringSize =
    variant === "media" ? 72 : variant === "link" ? 44 : variant === "text" ? 4 : 26;
  const ringBg =
    variant === "media"
      ? "rgba(196,143,86,0.95)"
      : variant === "link"
      ? "rgba(255,255,255,0.15)"
      : "transparent";
  const ringBorder =
    variant === "text" ? "transparent" : "rgba(255,255,255,0.55)";

  return (
    <>
      {/* Ring (springed) */}
      <motion.div
        aria-hidden
        style={{
          x: ringX,
          y: ringY,
          width: ringSize,
          height: ringSize,
          backgroundColor: ringBg,
          borderColor: ringBorder,
          translateX: "-50%",
          translateY: "-50%",
        }}
        className="pointer-events-none fixed left-0 top-0 z-[9999] rounded-full border transition-[width,height,background-color,border-color] duration-200 ease-out mix-blend-difference"
      >
        {label && (
          <span className="absolute inset-0 grid place-items-center text-[9px] uppercase tracking-[0.25em] text-black font-mono font-medium">
            {label}
          </span>
        )}
      </motion.div>

      {/* Dot (raw, fast) */}
      <motion.div
        aria-hidden
        style={{
          x,
          y,
          translateX: "-50%",
          translateY: "-50%",
        }}
        className={`pointer-events-none fixed left-0 top-0 z-[9999] rounded-full bg-white ${
          variant === "media" || variant === "text" ? "opacity-0" : "opacity-100"
        } transition-opacity duration-200`}
      >
        <span className="block h-1 w-1" />
      </motion.div>
    </>
  );
}
